import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Input,
  Select,
  Pagination,
  Col,
  Row,
  Flex,
  Tag,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { capitalizeFirstLetter } from "../../utils/functions";

const { Option } = Select;

// ── Format ISO date strings ───────────────────────────────────────────────────
const formatDate = (val) => {
  if (!val) return "—";
  const str = String(val);
  // ISO format with T: "2025-11-28T06:52:52"
  const isoT = str.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (isoT) return `${isoT[3]}/${isoT[2]}/${isoT[1]}`;
  // Space-separated format: "2025-11-28 06:52:52.83827"
  const isoSpace = str.match(/^(\d{4})-(\d{2})-(\d{2})[ T]/);
  if (isoSpace) return `${isoSpace[3]}/${isoSpace[2]}/${isoSpace[1]}`;
  return str;
};

const DataTable = ({ dataSource, columns, sumColumns = [], ...rest }) => {
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filteredData, setFilteredData] = useState(dataSource);
  const [currentRecords, setCurrentRecords] = useState(dataSource);
  const [columnTotals, setColumnTotals] = useState({});
  const [statusFilter, setStatusFilter] = useState("All");
  const [fieldName, setFieldsName] = useState("");
  const dispatch = useDispatch();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };
    fetchProducts();
  }, [dispatch]);

  const calculateColumnTotals = (records) => {
    const totals = {};
    if (records) {
      sumColumns.forEach((columnName) => {
        const total = records.reduce(
          (sum, record) => sum + (parseFloat(record[columnName]) || 0),
          0,
        );
        totals[columnName] = total.toFixed(2);
      });
    }
    setColumnTotals(totals);
  };

  const getPagination = useCallback(
    (currentPage, pageSize, filteredRecords) => {
      const pageStart = (currentPage - 1) * pageSize + 1;
      const pageEnd =
        currentPage * pageSize > filteredRecords.length
          ? filteredRecords.length
          : currentPage * pageSize;
      return { pageStart: filteredRecords.length > 0 ? pageStart : 0, pageEnd };
    },
    [],
  );

  const [pageData, setPageData] = useState(
    getPagination(currentPage, pageSize, dataSource),
  );
  const handlePageChange = (page) => setCurrentPage(page);

  const searchTableData = (searchString, data) => {
    return data.filter((item) =>
      Object.values(item).some(
        (value) =>
          value &&
          typeof value === "string" &&
          value.toLowerCase().includes(searchString.toLowerCase()),
      ),
    );
  };

  const filterByStatus = (data, status, field = "") => {
    if (status === "Active") return data.filter((item) => item.status === 1);
    if (status === "Inactive") return data.filter((item) => item.status === 0);
    if (field && status) return data.filter((item) => item[field] === status);
    return data;
  };

  const handleStatusFilterChange = (status, field = "") => {
    setStatusFilter(status);
    setFieldsName(field);
    setCurrentPage(1);
  };

  useEffect(() => {
    let filteredRecordsFromSearch = searchTableData(searchText, dataSource ?? []);
    filteredRecordsFromSearch = filterByStatus(
      filteredRecordsFromSearch,
      statusFilter,
      fieldName,
    );
    const currentRecords = filteredRecordsFromSearch.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize,
    );
    setCurrentRecords(currentRecords);
    calculateColumnTotals(currentRecords);
    setFilteredData(filteredRecordsFromSearch);
    setPageData(
      getPagination(currentPage, pageSize, filteredRecordsFromSearch),
    );
  }, [dataSource, searchText, currentPage, pageSize, statusFilter]);

  const getCsvData = () =>
    currentRecords.map((item) => {
      const row = {};
      columns.forEach((col) => {
        row[col.dataIndex] = item[col.dataIndex];
      });
      return row;
    });

  // ── PDF EXPORT ──────────────────────────────────────────────────────────────
  // Strategy:
  //  • Page: A3 landscape (420 × 297 mm) — much wider than A4 landscape
  //  • Font size: 7pt for body so more text fits per line
  //  • overflow: "linebreak" with maxCellHeight limiting to ~4 lines worth
  //  • NO truncation — full data shown, wraps naturally up to 4 lines
  //  • Skip Action column (no useful print data)
  //  • Weighted column widths so long-text cols get more room
  const exportToPdf = () => {
    // A3 landscape gives ~400mm usable width vs ~277mm on A4 landscape
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a3",
    });
    const pageW = doc.internal.pageSize.getWidth(); // 420mm
    const pageH = doc.internal.pageSize.getHeight(); // 297mm

    const date = new Date();
    const formattedDate = `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
    const title = rest.title || "Data Report";

    // Skip Action / toggle columns — not useful in print
    const skipKeys = ["action", "isActive"];
    const printCols = columns.filter(
      (col) => !skipKeys.includes(col.dataIndex) && col.title !== "Action",
    );

    const margins = { left: 8, right: 8, top: 26, bottom: 12 };
    const usableW = pageW - margins.left - margins.right; // ~404mm

    // Weight-based column widths
    const longTextKeys = [
      "description",
      "detail",
      "notes",
      "remark",
      "address",
      "info",
    ];
    const dateKeys = ["at", "date", "on", "created", "updated"];

    const weights = printCols.map((col) => {
      const k = col.dataIndex?.toLowerCase() || "";
      if (longTextKeys.some((x) => k.includes(x))) return 5; // widest
      if (dateKeys.some((x) => k.includes(x))) return 2.2; // medium
      return 1.4; // short
    });
    const totalWeight = weights.reduce((a, b) => a + b, 0);

    // columnStyles for autoTable
    const columnStyles = {};
    printCols.forEach((col, i) => {
      const k = col.dataIndex?.toLowerCase() || "";
      const colW = +((weights[i] / totalWeight) * usableW).toFixed(1);
      columnStyles[i] = {
        cellWidth: colW,
        halign: longTextKeys.some((x) => k.includes(x)) ? "left" : "center",
        // linebreak = wrap words naturally (NO ellipsis/truncation)
        overflow: "linebreak",
      };
    });

    // Build clean cell values
    const rows = currentRecords.map((item) =>
      printCols.map((col) => {
        const val = item[col.dataIndex];
        if (val === undefined || val === null || val === "") return "—";
        const k = col.dataIndex?.toLowerCase() || "";
        if (dateKeys.some((x) => k.includes(x))) return formatDate(val);
        if (k === "status")
          return val === 1 ? "Active" : val === 0 ? "Inactive" : String(val);
        if (typeof val === "boolean") return val ? "Yes" : "No";
        if (val === "true") return "Yes";
        if (val === "false") return "No";
        return String(val);
      }),
    );

    // Totals row
    const hasTotals =
      sumColumns.length > 0 && Object.keys(columnTotals).length > 0;
    if (hasTotals) {
      rows.push(
        printCols.map((col, i) => {
          if (sumColumns.includes(col.dataIndex) && columnTotals[col.dataIndex])
            return columnTotals[col.dataIndex];
          return i === 0 ? "TOTAL" : "";
        }),
      );
    }

    // ── Title header bar ──
    doc.setFillColor(59, 91, 219);
    doc.rect(0, 0, pageW, 20, "F");
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text(title, margins.left, 13);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(180, 200, 255);
    doc.text(
      `Generated: ${formattedDate}   ·   Records: ${currentRecords.length}   ·   Filter: ${statusFilter}`,
      margins.left,
      18,
    );

    // ── autoTable ──
    doc.autoTable({
      head: [printCols.map((col) => col.title)],
      body: rows,
      startY: margins.top,
      margin: {
        left: margins.left,
        right: margins.right,
        bottom: margins.bottom,
      },
      tableWidth: usableW,
      columnStyles,

      styles: {
        font: "helvetica",
        fontSize: 7, // small enough to fit 4 lines in reasonable row height
        cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
        valign: "top", // top-align so multi-line reads naturally
        overflow: "linebreak", // FULL word wrap — no truncation
        lineColor: [210, 218, 238],
        lineWidth: 0.18,
        textColor: [35, 40, 52],
        minCellHeight: 7,
        // maxCellHeight limits each cell to ~4 lines:
        // 4 lines × (7pt font ÷ 2.83 pt/mm) + padding ≈ 14mm
        maxCellHeight: 28,
      },

      headStyles: {
        fillColor: [59, 91, 219],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 7.5,
        halign: "center",
        valign: "middle",
        cellPadding: { top: 4, right: 3, bottom: 4, left: 3 },
        lineWidth: 0,
        minCellHeight: 10,
      },

      alternateRowStyles: {
        fillColor: [245, 247, 255],
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
      },

      // Totals row styling
      didParseCell(data) {
        if (
          hasTotals &&
          data.section === "body" &&
          data.row.index === rows.length - 1
        ) {
          data.cell.styles.fillColor = [219, 228, 255];
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.textColor = [30, 60, 180];
        }
      },

      // Footer on every page
      didDrawPage(data) {
        const totalPages = doc.internal.getNumberOfPages();
        doc.setFontSize(7);
        doc.setTextColor(160, 160, 175);
        const footerY = pageH - 5;
        doc.text(
          `Page ${data.pageNumber} of ${totalPages}`,
          pageW / 2,
          footerY,
          { align: "center" },
        );
        doc.text(title, margins.left, footerY);
        doc.text(formattedDate, pageW - margins.right, footerY, {
          align: "right",
        });
      },
    });

    doc.save(
      `${title.replace(/\s+/g, "_")}_${formattedDate.replace(/\//g, "-")}.pdf`,
    );
  };

  // ── EXCEL EXPORT ────────────────────────────────────────────────────────────
  const exportToExcel = () => {
    const date = new Date();
    const formattedDate = `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
    const title = rest.title || "Data Report";
    const dataRows = currentRecords.map((record) =>
      columns.reduce((obj, column) => {
        const val = record[column.dataIndex];
        obj[column.title] = val !== undefined && val !== null ? val : "";
        return obj;
      }, {}),
    );
    const hasTotals =
      sumColumns.length > 0 && Object.keys(columnTotals).length > 0;
    if (hasTotals) {
      dataRows.push(
        columns.reduce((obj, column) => {
          if (
            sumColumns.includes(column.dataIndex) &&
            columnTotals[column.dataIndex]
          )
            obj[column.title] = parseFloat(columnTotals[column.dataIndex]);
          else obj[column.title] = column === columns[0] ? "TOTAL" : "";
          return obj;
        }, {}),
      );
    }
    const ws = XLSX.utils.json_to_sheet(dataRows);
    ws["!cols"] = columns.map((col) => ({
      wch: Math.min(
        Math.max(
          col.title.length + 4,
          ...currentRecords.map(
            (r) => String(r[col.dataIndex] ?? "").length + 2,
          ),
          12,
        ),
        40,
      ),
    }));
    ws["!freeze"] = {
      xSplit: 0,
      ySplit: 1,
      topLeftCell: "A2",
      activePane: "bottomLeft",
    };
    for (let c = 0; c < columns.length; c++) {
      const cellAddr = XLSX.utils.encode_cell({ r: 0, c });
      if (!ws[cellAddr]) continue;
      ws[cellAddr].s = {
        font: { bold: true, color: { rgb: "FFFFFF" }, name: "Arial", sz: 11 },
        fill: { fgColor: { rgb: "3B5BDB" }, patternType: "solid" },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: { bottom: { style: "medium", color: { rgb: "2244BB" } } },
      };
    }
    const rowCount = dataRows.length;
    for (let r = 1; r <= rowCount; r++) {
      const isTotalsRow = hasTotals && r === rowCount;
      for (let c = 0; c < columns.length; c++) {
        const cellAddr = XLSX.utils.encode_cell({ r, c });
        if (!ws[cellAddr]) ws[cellAddr] = { t: "s", v: "" };
        ws[cellAddr].s = {
          font: {
            name: "Arial",
            sz: 10,
            bold: isTotalsRow,
            color: { rgb: isTotalsRow ? "1E3CB4" : "1E1E1E" },
          },
          fill: {
            fgColor: {
              rgb: isTotalsRow ? "DBE4FF" : r % 2 === 0 ? "F5F7FF" : "FFFFFF",
            },
            patternType: "solid",
          },
          alignment: { vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "DCE1EB" } },
            bottom: { style: "thin", color: { rgb: "DCE1EB" } },
            left: { style: "thin", color: { rgb: "DCE1EB" } },
            right: { style: "thin", color: { rgb: "DCE1EB" } },
          },
        };
      }
    }
    const metaWs = XLSX.utils.aoa_to_sheet([
      ["Report Title", title],
      ["Export Date", formattedDate],
      ["Total Records", currentRecords.length],
      ["Filter", statusFilter !== "All" ? statusFilter : "None"],
    ]);
    metaWs["!cols"] = [{ wch: 18 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.utils.book_append_sheet(wb, metaWs, "Info");
    const excelBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });
    FileSaver.saveAs(
      new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      }),
      `${title.replace(/\s+/g, "_")}_${formattedDate}.xlsx`,
    );
  };

  // ── TABLE COLUMN RENDERING ──────────────────────────────────────────────────
  const longTextKeys = [
    "description",
    "detail",
    "notes",
    "remark",
    "address",
    "info",
  ];
  const dateKeys = ["at", "date", "on"];

  const updatedColumns = columns.map((column) => {
    const baseTitle =
      sumColumns.includes(column.dataIndex) && columnTotals[column.dataIndex]
        ? `${column.title} (Total: ${columnTotals[column.dataIndex]})`
        : column.title;
    const base = { ...column, title: baseTitle };
    const k = column.dataIndex?.toLowerCase() || "";

    if (longTextKeys.some((x) => k.includes(x))) {
      return {
        ...base,
        width: 220,
        ellipsis: false,
        render: (text) =>
          text ? (
            <Tooltip
              title={text}
              placement="topLeft"
              overlayStyle={{ maxWidth: 360 }}
            >
              <div
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: "1.5",
                  fontSize: "0.81rem",
                  color: "#495057",
                  cursor: "default",
                  maxWidth: 220,
                }}
              >
                {text}
              </div>
            </Tooltip>
          ) : (
            <span style={{ color: "#ced4da" }}>—</span>
          ),
      };
    }

    if (dateKeys.some((x) => k.includes(x))) {
      return {
        ...base,
        width: 105,
        align: "center",
        render: (val) => (
          <span
            style={{
              fontSize: "0.8rem",
              color: "#495057",
              whiteSpace: "nowrap",
            }}
          >
            {formatDate(val)}
          </span>
        ),
      };
    }

    if (k === "status") {
      return {
        ...base,
        width: 90,
        align: "center",
        render: (val) =>
          val === 1 ? (
            <Tag
              style={{
                borderRadius: 20,
                fontWeight: 700,
                fontSize: "0.7rem",
                padding: "2px 10px",
                background: "#ebfbee",
                color: "#2f9e44",
                border: "1px solid #b2f2bb",
                margin: 0,
              }}
            >
              Active
            </Tag>
          ) : (
            <Tag
              style={{
                borderRadius: 20,
                fontWeight: 700,
                fontSize: "0.7rem",
                padding: "2px 10px",
                background: "#f8f9fa",
                color: "#868e96",
                border: "1px solid #dee2e6",
                margin: 0,
              }}
            >
              Inactive
            </Tag>
          ),
      };
    }

    if (k === "demo" || k === "isdemo") {
      return {
        ...base,
        width: 75,
        align: "center",
        render: (val) => {
          const isYes = val === true || val === "true";
          return (
            <Tag
              style={{
                borderRadius: 20,
                fontSize: "0.68rem",
                padding: "1px 8px",
                background: isYes ? "#e7f5ff" : "#f8f9fa",
                color: isYes ? "#1971c2" : "#868e96",
                border: `1px solid ${isYes ? "#a5d8ff" : "#dee2e6"}`,
                fontWeight: 600,
                margin: 0,
              }}
            >
              {isYes ? "Yes" : "No"}
            </Tag>
          );
        },
      };
    }

    if (k === "action" || base.title === "Action") {
      return { ...base, width: 85, align: "center", fixed: "right" };
    }

    if (k === "name") {
      return {
        ...base,
        width: 120,
        render: (val) => (
          <span
            style={{ fontWeight: 600, color: "#343a40", fontSize: "0.84rem" }}
          >
            {val || "—"}
          </span>
        ),
      };
    }

    const shortKeys2 = [
      "discount",
      "gross",
      "amc",
      "period",
      "type",
      "business",
    ];
    if (shortKeys2.some((x) => k.includes(x))) {
      return { ...base, width: 100, align: "center" };
    }

    return { ...base, align: "center" };
  });

  return (
    <div {...rest}>
      <style>{`
        .dt-toolbar {
          background: #ffffff;
          border: 1px solid #e4e8f0;
          border-bottom: none;
          border-radius: 12px 12px 0 0;
          padding: 14px 18px 12px;
        }
        .dt-table-wrap .ant-table {
          font-size: 0.83rem;
          border-left: 1px solid #e4e8f0;
          border-right: 1px solid #e4e8f0;
        }
        .dt-table-wrap .ant-table-thead > tr > th {
          background: #f0f3ff !important;
          color: #3b5bdb !important;
          font-weight: 700 !important;
          font-size: 0.72rem !important;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          padding: 10px 12px !important;
          border-bottom: 2px solid #c5d0fa !important;
          white-space: nowrap;
        }
        .dt-table-wrap .ant-table-thead > tr > th::before { display: none !important; }
        .dt-table-wrap .ant-table-tbody > tr > td {
          padding: 8px 12px !important;
          border-bottom: 1px solid #eef0f8 !important;
          vertical-align: middle !important;
          transition: background 0.1s;
        }
        .dt-table-wrap .ant-table-tbody > tr:nth-child(even) > td { background: #f7f8ff; }
        .dt-table-wrap .ant-table-tbody > tr:nth-child(odd) > td  { background: #ffffff; }
        .dt-table-wrap .ant-table-tbody > tr:hover > td { background: #e8edff !important; }
        .dt-table-wrap .ant-table-cell-fix-right {
          box-shadow: -3px 0 10px rgba(59,91,219,0.06) !important;
        }
        .dt-table-wrap .ant-table-tbody > tr:hover .ant-table-cell-fix-right { background: #e8edff !important; }
        .dt-table-wrap .ant-table-tbody > tr:nth-child(even) .ant-table-cell-fix-right { background: #f7f8ff; }
        .dt-table-wrap .ant-table-tbody > tr:nth-child(odd)  .ant-table-cell-fix-right { background: #ffffff; }
        .dt-table-wrap .ant-table-column-sorter-up.active,
        .dt-table-wrap .ant-table-column-sorter-down.active { color: #3b5bdb; }
        .dt-pagination-bar {
          background: #ffffff;
          border: 1px solid #e4e8f0;
          border-top: 1px solid #eef0f8;
          border-radius: 0 0 12px 12px;
          padding: 10px 18px;
        }
        .dt-pagination-bar .ant-pagination-item-active {
          border-color: #3b5bdb !important;
          background: #3b5bdb !important;
          border-radius: 6px;
        }
        .dt-pagination-bar .ant-pagination-item-active a { color: #fff !important; }
        .dt-pagination-bar .ant-pagination-item { border-radius: 6px; }
        .dt-pagination-bar .ant-pagination-item:hover { border-color: #3b5bdb; }
        .dt-pagination-bar .ant-pagination-item:hover a { color: #3b5bdb; }
        .dt-export-btn {
          height: 30px !important; padding: 0 13px !important;
          font-size: 0.74rem !important; font-weight: 700 !important;
          border-radius: 7px !important; letter-spacing: 0.04em;
          display: inline-flex !important; align-items: center !important;
          gap: 5px; border: none !important;
          box-shadow: 0 2px 6px rgba(0,0,0,0.12) !important;
          transition: opacity 0.15s, transform 0.1s !important;
        }
        .dt-export-btn:hover { opacity: 0.88 !important; transform: translateY(-1px); }
        .dt-export-csv { background: #099268 !important; }
        .dt-export-xl  { background: #1864ab !important; }
        .dt-export-pdf { background: #c92a2a !important; }
        .dt-filter-pill {
          height: 28px !important; font-size: 0.72rem !important;
          font-weight: 700 !important; border-radius: 20px !important;
          padding: 0 14px !important; letter-spacing: 0.04em;
          border: none !important; box-shadow: 0 1px 4px rgba(0,0,0,0.1) !important;
        }
        .dt-search .ant-input-affix-wrapper {
          border-radius: 8px !important; border: 1px solid #dee2e6 !important;
          font-size: 0.82rem !important;
          transition: border-color 0.15s, box-shadow 0.15s !important;
        }
        .dt-search .ant-input-affix-wrapper:focus-within {
          border-color: #3b5bdb !important;
          box-shadow: 0 0 0 3px rgba(59,91,219,0.12) !important;
        }
        .dt-search .ant-input { font-size: 0.82rem !important; }
      `}</style>

      {/* ── TOOLBAR ── */}
      <div className="dt-toolbar">
        <Row gutter={[10, 10]} align="middle">
          <Col xs={24} sm={14} md={16}>
            <Flex align="center" gap={8}>
              <span
                style={{
                  fontSize: "0.79rem",
                  color: "#868e96",
                  whiteSpace: "nowrap",
                }}
              >
                Show
              </span>
              <Select
                defaultValue={10}
                size="small"
                onChange={(value) => {
                  setPageSize(value === 0 ? dataSource.length : value);
                  setCurrentPage(1);
                }}
                style={{ width: 68 }}
              >
                <Option value={10}>10</Option>
                <Option value={20}>20</Option>
                <Option value={30}>30</Option>
                <Option value={40}>40</Option>
                <Option value={0}>All</Option>
              </Select>
              <span style={{ fontSize: "0.79rem", color: "#868e96" }}>
                entries
              </span>
            </Flex>
          </Col>

          <Col xs={24} sm={10} md={8}>
            <Input
              className="dt-search"
              placeholder="Search records…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              suffix={
                <SearchOutlined style={{ color: "#adb5bd", fontSize: 13 }} />
              }
              size="small"
            />
          </Col>

          <Col xs={24}>
            <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
              <Flex gap={6} wrap="wrap" align="center">
                {location.pathname !== "/glGroup" && (
                  <>
                    <Button
                      type="primary"
                      size="small"
                      className="dt-filter-pill"
                      style={{ background: "#7048e8" }}
                      onClick={() => handleStatusFilterChange("All")}
                    >
                      All
                    </Button>

                    {rest.filterBy?.length > 0 &&
                      rest.filterBy.map((f, i) => {
                        if (f.fieldType === "SELECT")
                          return (
                            <Select
                              key={i}
                              size="small"
                              placeholder={capitalizeFirstLetter(f.label)}
                              allowClear
                              showSearch
                              style={{ minWidth: 130 }}
                              filterOption={(input, option) =>
                                option.children
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                              }
                              onChange={(e) =>
                                handleStatusFilterChange(e, f.field)
                              }
                            >
                              {f.options.map((opt) => (
                                <Select.Option key={opt.id} value={opt.id}>
                                  {opt.name}
                                </Select.Option>
                              ))}
                            </Select>
                          );
                        return (
                          <Button
                            key={i}
                            type="primary"
                            size="small"
                            className="dt-filter-pill"
                            style={{ background: "#2f9e44" }}
                            onClick={() =>
                              handleStatusFilterChange(f.filter, f.field)
                            }
                          >
                            {f.filter}
                          </Button>
                        );
                      })}

                    {rest.isActive && (
                      <>
                        <Button
                          type="primary"
                          size="small"
                          className="dt-filter-pill"
                          style={{ background: "#2f9e44" }}
                          onClick={() => handleStatusFilterChange("Active")}
                        >
                          Active
                        </Button>
                        <Button
                          type="primary"
                          size="small"
                          className="dt-filter-pill"
                          style={{ background: "#c92a2a" }}
                          onClick={() => handleStatusFilterChange("Inactive")}
                        >
                          Inactive
                        </Button>
                      </>
                    )}
                  </>
                )}
              </Flex>

              <Flex gap={6}>
                <CSVLink data={getCsvData()} filename={"data.csv"}>
                  <Button
                    type="primary"
                    size="small"
                    className="dt-export-btn dt-export-csv"
                  >
                    <FileTextOutlined /> CSV
                  </Button>
                </CSVLink>
                <Button
                  type="primary"
                  size="small"
                  className="dt-export-btn dt-export-xl"
                  onClick={exportToExcel}
                >
                  <FileExcelOutlined /> Excel
                </Button>
                <Button
                  type="primary"
                  size="small"
                  className="dt-export-btn dt-export-pdf"
                  onClick={exportToPdf}
                >
                  <FilePdfOutlined /> PDF
                </Button>
              </Flex>
            </Flex>
          </Col>
        </Row>
      </div>

      {/* ── TABLE ── */}
      <div className="dt-table-wrap">
        <Table
          dataSource={currentRecords}
          columns={updatedColumns}
          pagination={false}
          rowKey={(record) => record.key}
          scroll={{ x: "max-content" }}
          size="small"
          exportable
          exportableProps={{ showColumnPicker: true }}
        />
      </div>

      {/* ── PAGINATION ── */}
      <div className="dt-pagination-bar">
        <Row gutter={16} align="middle">
          <Col span={12}>
            <span style={{ fontSize: "0.79rem", color: "#868e96" }}>
              Showing{" "}
              <strong style={{ color: "#495057" }}>
                {pageData?.pageStart}
              </strong>
              {" – "}
              <strong style={{ color: "#495057" }}>{pageData?.pageEnd}</strong>
              {" of "}
              <strong style={{ color: "#3b5bdb" }}>
                {filteredData.length}
              </strong>
              {" entries"}
            </span>
          </Col>
          <Col span={12} style={{ textAlign: "right" }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={filteredData.length}
              onChange={handlePageChange}
              showSizeChanger={false}
              size="small"
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default DataTable;
